import { ApiError } from '@/api/client'

/**
 * Why a chunk append failed — and therefore whether the progressive stream is
 * still worth keeping alive (CLA-945 Phase 3).
 *
 * The old rule was "any error ⇒ abandon progressive", which is **correct when
 * the chunk stream is corrupt and wrong when the API is merely unavailable**.
 * During an API degradation, failing chunk appends *are* the symptom — so the
 * outage case was precisely the case that abandoned the server-side partial
 * copy and dropped onto the unprotected path.
 *
 * With CLA-938 the audio is no longer at risk either way; this is about
 * recovery *speed* and about how much of the consult the server still holds if
 * the tab dies mid-outage.
 */
export type AppendFailure =
  /** The bytes never arrived: offline, DNS, timeout, 502/503/504, aborted. */
  | 'transport'
  /**
   * The server received the request and rejected the STREAM: a reported gap,
   * a malformed chunk, or a note that no longer exists. Appending more cannot
   * repair it, so fall back to the atomic upload immediately.
   */
  | 'integrity'

/**
 * Classify an append failure.
 *
 * Deliberately conservative in one direction: anything not clearly the
 * server's judgement on the stream is treated as transport, because the cost
 * of a wrong `transport` is a few retried chunks, while the cost of a wrong
 * `integrity` is abandoning the server-side copy during an outage — the exact
 * bug this fixes.
 */
export function classifyAppendFailure(error: unknown): AppendFailure {
  if (!(error instanceof ApiError)) {
    // A thrown TypeError from fetch is the browser's way of saying "the
    // request never completed" — offline, DNS, CORS, connection reset.
    return 'transport'
  }
  // 408 Request Timeout and 429 Too Many Requests are explicitly retryable,
  // and 5xx means the server failed to process, not that the stream is bad.
  if (error.status === 408 || error.status === 429 || error.status >= 500) return 'transport'
  // A 0 status is how some clients surface an aborted/blocked request.
  if (error.status === 0) return 'transport'
  // Everything else in 4xx is the server judging this request: 400 malformed
  // chunk, 404 note gone, 409 conflicting index, 422 unprocessable.
  return 'integrity'
}

/**
 * Retries for a single chunk before the stream is given up on.
 *
 * Kept deliberately small. Appends are serialised on the capture's upload
 * chain, so a retrying chunk holds up every chunk behind it and their blobs
 * stay in memory. Two quick retries ride out a blip; anything longer would
 * turn a degradation into a memory problem during a live consult.
 */
export const APPEND_RETRIES = 2

/** Short, fixed backoff between chunk retries — a blip, not an outage. */
export const APPEND_RETRY_DELAY_MS = 750

/**
 * Consecutive failed chunks tolerated before progressive mode is abandoned.
 *
 * This is the circuit-breaker that separates "a blip" from "the API is down".
 * Without it, a sustained outage would have every chunk burn its retries while
 * the queue and its blobs grow behind it — the load-shedding that the old
 * fail-fast behaviour got right, and which must survive this change.
 */
export const MAX_CONSECUTIVE_CHUNK_FAILURES = 3
